import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Icon,
  Button,
  Card,
  CardHeader,
  CardBody,
  Input,
  Badge,
  showToast,
} from '@repo/ui';
import { PageLayout } from '@xtnd/web/shared/layout';
import {
  useAllocationsReplicationMetadata,
  useUpdateAllocationsReplicationMetadata,
  useSyncAllocationFromClio,
} from '../queries';
import {
  dataReplicationApi,
  type AllocationSearchType,
  type AllocationSearchResult,
} from '../api';
import { canResetReplication } from '../utils/accessControl';
import { pickReplicationTimestamp } from '../utils/replication';

type SearchTypeOption = {
  value: AllocationSearchType;
  label: string;
};

const SEARCH_TYPE_OPTIONS: SearchTypeOption[] = [
  { value: 'allocationId', label: 'Allocation ID' },
  { value: 'matterId', label: 'Matter ID' },
  { value: 'billId', label: 'Bill ID' },
  { value: 'contactId', label: 'Contact ID' },
];

export default function ClioAllocationsReplicationPage() {
  const navigate = useNavigate();

  // State
  const [searchValue, setSearchValue] = useState('');
  const [searchType, setSearchType] = useState<AllocationSearchType>('allocationId');
  const [isSearching, setIsSearching] = useState(false);
  const [dynamoDBResult, setDynamoDBResult] = useState<AllocationSearchResult | null>(null);
  const [clioResult, setClioResult] = useState<AllocationSearchResult | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Queries and mutations
  const { data: metadataResponse, isLoading: isLoadingMetadata, refetch } = useAllocationsReplicationMetadata();
  const updateMetadataMutation = useUpdateAllocationsReplicationMetadata();
  const syncAllocationMutation = useSyncAllocationFromClio();

  const metadata = metadataResponse?.metadata;
  const allocationsCount = metadataResponse?.allocationsCount ?? 0;
  const canReset = canResetReplication();

  // Format date helper
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  // Handle reset metadata
  const handleResetMetadata = async () => {
    try {
      await updateMetadataMutation.mutateAsync(null);
    } catch (error) {
      // Error handled in mutation hook
    }
  };

  // Handle search
  const handleSearch = async () => {
    if (!searchValue.trim()) {
      showToast.error('Please enter a search value');
      return;
    }

    setIsSearching(true);
    setHasSearched(true);
    setDynamoDBResult(null);
    setClioResult(null);

    try {
      // First search in DynamoDB
      const dbResult = await dataReplicationApi.searchAllocationInDynamoDB(searchValue.trim(), searchType);
      setDynamoDBResult(dbResult);

      // If not found in DynamoDB, search in Clio
      if (!dbResult.found) {
        const clResult = await dataReplicationApi.searchAllocationInClio(searchValue.trim(), searchType);
        setClioResult(clResult);
      }
    } catch (error: any) {
      showToast.error(`Search failed: ${error.message}`);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle sync/resync allocation
  const handleSyncAllocation = async (allocationId: string) => {
    try {
      await syncAllocationMutation.mutateAsync(allocationId);
      // Re-run the search to show updated data
      await handleSearch();
    } catch (error) {
      // Error handled in mutation hook
    }
  };

  // Render allocation details
  const renderAllocationDetails = (allocation: any, source: string) => {
    return (
      <div className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-medium text-xtnd-dark-800 dark:text-white">
            Allocation Details
          </h4>
          <Badge variant={source === 'dynamodb' ? 'success' : 'warning'}>
            {source === 'dynamodb' ? 'From XTND' : 'From Clio'}
          </Badge>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">Allocation ID:</span>
            <p className="font-medium text-xtnd-dark-800 dark:text-white">
              {allocation.allocationId || allocation.id || 'N/A'}
            </p>
          </div>
          <div>
            <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">Matter ID:</span>
            <p className="font-medium text-xtnd-dark-800 dark:text-white">
              {allocation.matterId || allocation.matter?.id || 'N/A'}
            </p>
          </div>
          <div>
            <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">Bill ID:</span>
            <p className="font-medium text-xtnd-dark-800 dark:text-white">
              {allocation.billId || allocation.bill?.id || 'N/A'}
            </p>
          </div>
          <div>
            <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">Contact ID:</span>
            <p className="font-medium text-xtnd-dark-800 dark:text-white">
              {allocation.contactId || allocation.contact?.id || 'N/A'}
            </p>
          </div>
          <div>
            <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">Amount:</span>
            <p className="font-medium text-xtnd-dark-800 dark:text-white">
              {allocation.amount || allocation.total || 'N/A'}
            </p>
          </div>
          {source === 'dynamodb' && allocation.replicatedAt && (
            <div className="col-span-2 md:col-span-3">
              <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">Last Replicated:</span>
              <p className="font-medium text-xtnd-dark-800 dark:text-white">
                {formatDate(allocation.replicatedAt)}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <PageLayout
      title="Clio Allocations Replication"
      description="Manage and monitor Clio allocations data replication"
      icon="HiDatabase"
      isLoading={isLoadingMetadata}
      breadcrumbs={[
        { label: 'Settings', href: '/settings' },
        { label: 'Data Replication' },
        { label: 'Clio Allocations' },
      ]}
    >
      <div className="space-y-6">
        {/* Back button */}
        <Button
          variant="outline"
          size="sm"
          leftIcon={<Icon name="HiArrowLeft" className="h-4 w-4" />}
          onClick={() => navigate('/settings')}
        >
          Back to Settings
        </Button>

        {/* Replication Status Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon name="HiInformationCircle" className="h-6 w-6 text-xtnd-blue" />
                <h3 className="text-lg font-medium text-xtnd-dark-800 dark:text-white">
                  Replication Status
                </h3>
              </div>
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Icon name="HiRefresh" className="h-4 w-4" />}
                onClick={() => refetch()}
              >
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            <div className={`grid grid-cols-1 ${canReset ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6`}>
              {/* Allocations Count */}
              <div className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <Icon name="HiCollection" className="h-8 w-8 text-xtnd-blue" />
                  <div>
                    <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
                      Total Allocations
                    </p>
                    <p className="text-2xl font-bold text-xtnd-dark-800 dark:text-white">
                      {allocationsCount.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Last Replication */}
              <div className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <Icon name="HiClock" className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
                      Last Replication
                    </p>
                    <p className="text-sm font-medium text-xtnd-dark-800 dark:text-white">
                      {formatDate(pickReplicationTimestamp(metadata))}
                    </p>
                  </div>
                </div>
              </div>

              {/* Reset Action - Only visible to authorized users */}
              {canReset && (
                <div className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700 rounded-lg p-4">
                  <div className="flex flex-col space-y-2">
                    <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
                      Reset Replication
                    </p>
                    <p className="text-xs text-xtnd-dark-300 dark:text-xtnd-dark-400 mb-2">
                      Clear the last replication date to force a full sync on the next scheduled run
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleResetMetadata}
                      loading={updateMetadataMutation.isPending}
                      leftIcon={<Icon name="HiRefresh" className="h-4 w-4" />}
                    >
                      Reset to Force Full Sync
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Allocation Search Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-3">
              <Icon name="HiSearch" className="h-6 w-6 text-xtnd-blue" />
              <h3 className="text-lg font-medium text-xtnd-dark-800 dark:text-white">
                Search & Sync Allocation
              </h3>
            </div>
          </CardHeader>
          <CardBody>
            <div className="space-y-4">
              {/* Search Form */}
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Search Type Dropdown */}
                <div className="sm:w-48">
                  <label className="block text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200 mb-1">
                    Search By
                  </label>
                  <select
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value as AllocationSearchType)}
                    className="w-full px-3 py-2 border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md bg-white dark:bg-xtnd-dark-700 text-xtnd-dark-800 dark:text-white focus:ring-xtnd-blue focus:border-xtnd-blue"
                  >
                    {SEARCH_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Search Input */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200 mb-1">
                    Search Value
                  </label>
                  <Input
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder={`Enter ${SEARCH_TYPE_OPTIONS.find(o => o.value === searchType)?.label}...`}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>

                {/* Search Button */}
                <div className="flex items-end">
                  <Button
                    variant="primary"
                    onClick={handleSearch}
                    loading={isSearching}
                    leftIcon={<Icon name="HiSearch" className="h-4 w-4" />}
                  >
                    Search
                  </Button>
                </div>
              </div>

              {/* Search Results */}
              {hasSearched && (
                <div className="mt-6 space-y-4">
                  <h4 className="text-md font-medium text-xtnd-dark-800 dark:text-white">
                    Search Results
                  </h4>

                  {/* Loading state */}
                  {isSearching && (
                    <div className="text-center py-8">
                      <Icon name="HiRefresh" className="h-8 w-8 text-xtnd-blue animate-spin mx-auto" />
                      <p className="mt-2 text-xtnd-dark-400 dark:text-xtnd-dark-300">Searching...</p>
                    </div>
                  )}

                  {/* DynamoDB Result */}
                  {!isSearching && dynamoDBResult && (
                    <div className="space-y-4">
                      {dynamoDBResult.found && dynamoDBResult.allocation ? (
                        <>
                          {renderAllocationDetails(dynamoDBResult.allocation, 'dynamodb')}
                          <div className="flex justify-end">
                            <Button
                              variant="secondary"
                              onClick={() => handleSyncAllocation(
                                dynamoDBResult.allocation?.allocationId ||
                                dynamoDBResult.allocation?.id?.toString() ||
                                searchValue
                              )}
                              loading={syncAllocationMutation.isPending}
                              leftIcon={<Icon name="HiRefresh" className="h-4 w-4" />}
                            >
                              Resync from Clio
                            </Button>
                          </div>
                        </>
                      ) : (
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                          <div className="flex items-center space-x-2">
                            <Icon name="HiExclamation" className="h-5 w-5 text-yellow-500" />
                            <p className="text-sm text-yellow-700 dark:text-yellow-300">
                              Allocation not found in local cache (DynamoDB)
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Clio Result (only shown if not in DynamoDB) */}
                  {!isSearching && !dynamoDBResult?.found && clioResult && (
                    <div className="space-y-4">
                      {clioResult.found && clioResult.allocation ? (
                        <>
                          {renderAllocationDetails(clioResult.allocation, 'clio')}
                          <div className="flex justify-end">
                            <Button
                              variant="primary"
                              onClick={() => handleSyncAllocation(
                                clioResult.allocation?.id?.toString() || searchValue
                              )}
                              loading={syncAllocationMutation.isPending}
                              leftIcon={<Icon name="HiDownload" className="h-4 w-4" />}
                            >
                              Replicate to Local Cache
                            </Button>
                          </div>
                        </>
                      ) : (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                          <div className="flex items-center space-x-2">
                            <Icon name="HiXCircle" className="h-5 w-5 text-red-500" />
                            <p className="text-sm text-red-700 dark:text-red-300">
                              Allocation not found in Clio
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* No results anywhere */}
                  {!isSearching && !dynamoDBResult?.found && !clioResult?.found && clioResult !== null && (
                    <div className="text-center py-8">
                      <Icon name="HiSearchCircle" className="h-12 w-12 text-xtnd-dark-300 mx-auto" />
                      <p className="mt-2 text-xtnd-dark-400 dark:text-xtnd-dark-300">
                        No allocation found with the provided search criteria
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      </div>
    </PageLayout>
  );
}

